import React, { Fragment, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import ReCAPTCHA from "react-google-recaptcha";
import { verifyRecaptcha } from "../../api";
import { Spinner, ISpinnerStyles } from "@fluentui/react";

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
}

export const Disclaimer = ({ open, setOpen }: Props) => {
    const [reCaptchaToken, setReCaptchaToken] = useState("");

    // reCAPTCHA settings
    const handleRecaptcha = (value: any) => {
        setReCaptchaToken(value);
    };
    const [loading, setLoading] = useState(false);
    const focusButtonRef = useRef(null);
    const siteKey = import.meta.env.VITE_SITE_KEY;

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true); // Show a loading indicator

        try {
            const response = await verifyRecaptcha(reCaptchaToken);
            if (!response) {
                throw Error("No response body");
            }
            if ("token" in response) {
                const token = response.token;
                sessionStorage.setItem("token", token);
                setOpen(false);
            } else {
                throw Error("Invalid response");
            }
        } catch (error) {
            console.error("reCAPTCHA verification failed", error);
        } finally {
            setLoading(false); // Hide the loading indicator
        }
    };

    const spinnerStyles: ISpinnerStyles = {
        circle: {
            borderColor: "#FFFFFF #FFFFFF30 #FFFFFF30",
            height: "20px",
            width: "20px"
        }
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" initialFocus={focusButtonRef} onClose={() => {}}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="disclaimer fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center items-center sm:p-0">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="relative transform overflow-hidden bg-j-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-[50rem] rounded-xl">
                                <div className="px-4 pt-4 pb-3 sm:px-6">
                                    <div className="sm:flex sm:items-start">
                                        <div className="mt-0 text-left">
                                            <div className="mt-2 generic-content text-j-dark">
                                                <DialogTitle className="text-[20px] font-semibold leading-6 text-j-dark">Overview</DialogTitle>
                                                <p>
                                                    We provide a self-help platform that is powered by AI. The information and guidance this tool creates is to
                                                    support you in your research of the Jersey Financial Service Commission's financial regulatory regimes
                                                    (including AML/CFT/CPF). Information generated by the tool (in response to your inputs) is wholly AI
                                                    generated and also influenced by your inputs. The information the tool provides may therefore not be
                                                    complete or accurate. We encourage you to check all responses it creates (including by following any links
                                                    or references provided) and ensure they are accurate. You must ensure you meet any regulatory obligations
                                                    yourself and must not rely on the information generated by the tool to meet these obligations but seek
                                                    independent professional or legal advice where appropriate.
                                                </p>
                                                <DialogTitle className="text-[20px] font-semibold leading-6 text-j-dark">Terms and Conditions</DialogTitle>
                                                <p>
                                                    These Terms and Conditions (the <strong>“Terms”</strong>) govern your use of [Reggie The JFSC Chatbot] (the{" "}
                                                    <strong>“Chatbot”</strong>), owned and operated by the Jersey Financial Services Commission (
                                                    <strong>“we”</strong>, or <strong>“us”</strong>). <strong>“User”</strong> or <strong>“you”</strong> means a
                                                    user of the Chatbot, being an individual or legal entity.
                                                </p>
                                                <p>
                                                    By using the Chatbot, you hereby agree to be bound by these Terms and all applicable laws and/or
                                                    regulations, which may be in effect from time to time, and you agree that you are responsible and liable for
                                                    this compliance.
                                                </p>
                                                <p>
                                                    We reserve the right to change these Terms and any other policy applicable to the use of the Chatbot. These
                                                    changes are effective immediately upon posting. When changes come into effect, the revised Terms shall
                                                    supersede the previous version of the Terms. By continuing to use the Chatbot after modifications enter into
                                                    force, the User indicates to agree to be bound by such modifications.
                                                </p>
                                                <p>
                                                    <strong>1 Acceptance of the Terms</strong>
                                                </p>
                                                <p>
                                                    1.1 By using the Chatbot, you agree to proceed on the basis of these Terms and all applicable laws and
                                                    regulations.
                                                </p>
                                                <p>
                                                    1.2 If you do not agree to be bound by these Terms and any applicable laws and regulations, you must not use
                                                    the Chatbot.
                                                </p>
                                                <p>
                                                    <strong>2 Description of the Service</strong>
                                                </p>
                                                <p>
                                                    2.1 The Chatbot is a GenAI powered self-help system that enables you to enter search terms, questions or
                                                    other input to support you in your research and identify potentially relevant provisions and principles in
                                                    the JFSC's Codes of Practice, including the Anti-Money Laundering/Countering the Financing of
                                                    Terrorism/Countering Proliferation Financing Handbook (together <strong>“the Codes”</strong>).
                                                </p>
                                                <p>
                                                    2.2 You shall follow the steps given on our website (
                                                    <a href="https://www.jerseyfsc.org">www.jerseyfsc.org</a>) (the <strong>“Website”</strong>) on how to use
                                                    the Chatbot.
                                                </p>
                                                <p>2.3 You are fully responsible for the accuracy of the inputs you provide to the Chatbot.</p>
                                                <p>2.4 You can use the Chatbot as many times as you wish unless otherwise restricted by us.</p>
                                                <p>
                                                    <strong>3 Disclaimer and Limitation of Liability</strong>
                                                </p>
                                                <p>
                                                    3.1 You acknowledge and agree that the information generated by the Chatbot (in response to your inputs) is
                                                    wholly AI generated, influenced by your inputs and determined by probabilities rather than an understanding
                                                    of your questions or the Codes. The information the Chatbot provides may therefore not be complete or
                                                    accurate.
                                                </p>
                                                <p>
                                                    3.2 The Chatbot is only intended as a self-help tool to help guide you in your research of the Codes and you
                                                    must verify the information it provides (including by accessing any references the Chatbot may provide to
                                                    relevant provisions of the Codes) and, where appropriate, get independent professional or legal advice. It
                                                    should be the start of your research, not the end.
                                                </p>
                                                <p>
                                                    3.3 The information provided by the Chatbot shall not constitute legal advice or definitive regulatory
                                                    opinion/advice and is not intended to create a statement by us or to be relied on by you. You must ensure
                                                    you meet any regulatory obligations yourself and must not rely on the information generated by the Chatbot
                                                    to meet those obligations.
                                                </p>
                                                <p>
                                                    3.4 Under no circumstances shall we, including our licensors, be liable to you, any person or entity
                                                    whatsoever for any direct, indirect, incidental, consequential or punitive damages or any damages or losses
                                                    whatsoever. You agree to hold us harmless from any damage, claims and/or controversies that have arisen out
                                                    of or in connection with your use of or inability to use the Chatbot.
                                                </p>
                                                <p>
                                                    <strong>4 Restrictions on use</strong>
                                                </p>
                                                <p>
                                                    4.1 You acknowledge and agree that we or our licensors are the owners of all text, graphics, data,
                                                    formatting, graphs, designs, HTML, look and feel, designs, typefaces, and other content together with any
                                                    intellectual property rights subsisting in it (collectively <strong>“Proprietary Material”</strong>)
                                                    available on or generated from time to time through the Website and the Chatbot. You acknowledge and agree
                                                    that Proprietary Material is protected in all forms, media and technologies now known or hereinafter
                                                    developed. The Proprietary Material is protected by the domestic and international laws of copyright,
                                                    patents, and other proprietary rights and laws.
                                                </p>
                                                <p>4.2 You must not, whether directly or indirectly:</p>
                                                <p className="pl-[28px]">
                                                    4.2.1 use the Website and/or Chatbot for any illegal, malicious or other purpose in violation of these terms
                                                    or applicable laws and regulations;
                                                </p>
                                                <p className="pl-[28px]">
                                                    4.2.2 copy, distribute or resell the information generated by the Chatbot or compile or collect any such
                                                    information as part of a database or other work;
                                                </p>
                                                <p className="pl-[28px]">
                                                    4.2.3 use Proprietary Material, other than as permitted in these Terms, without our prior written consent.
                                                </p>
                                                <p>
                                                    4.3 The Chatbot is provided <strong>“AS IS”</strong> and may be inaccessible, inoperable or its use
                                                    restricted or limited in some other way due to equipment malfunctions, periodic maintenance, repairs, or
                                                    replacements and developments that we undertake from time to time.
                                                </p>
                                                <p>
                                                    4.4 We reserve the right to modify, suspend or terminate the Chatbot at any time for any reason with or
                                                    without prior notice to you or consent from you.
                                                </p>
                                                <p>
                                                    <strong>5 Miscellaneous</strong>
                                                </p>
                                                <p>
                                                    5.1 If any provision of these Terms is held to be invalid, non-binding, or unenforceable, the remaining
                                                    provisions shall remain valid and be enforced. In such event, such invalid provision shall nonetheless be
                                                    enforced to the fullest extent permitted by applicable law, and you will at least agree to accept a similar
                                                    effect as the invalid, unenforceable, or non-binding provision, given the contents and purpose of these
                                                    Terms.
                                                </p>
                                                <p>
                                                    5.2 These Terms shall be governed by and construed in accordance with the laws of Jersey, Channel Islands
                                                    without regard to conflict of law principles.
                                                </p>
                                                <p>
                                                    5.3 The courts of Jersey, Channel Islands shall settle any dispute arising out of or in connection with
                                                    these Terms.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-j-white px-4 pt-2 sm:pt-0 pb-2 sm:flex sm:flex-row-reverse sm:px-6">
                                    <ReCAPTCHA onChange={handleRecaptcha} sitekey={siteKey} />
                                </div>
                                <div className="bg-j-white px-4 pt-2 sm:pt-0 pb-2 sm:flex sm:flex-row-reverse sm:px-6">
                                    <div className="flex justify-center items-center my-4">
                                        <button
                                            type="button"
                                            onClick={handleSubmit}
                                            hidden={!reCaptchaToken}
                                            className="rounded-lg bg-j-green text-j-white hover:bg-j-green-hover"
                                        >
                                            {loading ? <Spinner style={{ display: "flex", verticalAlign: "middle" }} styles={spinnerStyles} /> : "I understand"}
                                        </button>
                                    </div>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
